import {
	idAssert,
	type JSONArray,
	type JSONData,
	type JSONObject,
	type JSONPartial,
} from '@lc/libts'

import { batch } from '@lc/libiter'
import { parallelRunner, SequentialRunner, type Runner } from '@lc/librunner'
import { eq, isSafeProp, safeProp } from '@lc/libutils'

export type ApiCallParams = {
	/**
	 * Data to send to the endpoint
	 * If a json is provided, it will be stringified and compressed if compression is enabled
	 */
	body?: FormData | Blob | ReadableStream | JSONPartial
	/**
	 * HTTP method to use for the capp
	 * Defaults to GET without body and POST with body
	 */
	method?: 'GET' | 'HEAD' | 'OPTIONS' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
	/**
	 * Query parameters to send in the call
	 */
	params?: { [_key: string]: JSONData | undefined }
	/**
	 * Additional headers to pass to the endpoint
	 */
	headers?: { [_key: string]: string | number | boolean }
	/**
	 * If set to true, the call will return the raw Response object
	 */
	raw?: boolean
	/**
	 * If set, the request will automatically fail if it doesnt resolve before that time
	 */
	timeout?: number
	/**
	 * If set, the keys param will be split into multiple calls
	 * Incompatible with raw
	 */
	bulk?: boolean | number
	/**
	 * If set, this runner will be used to dispatch the fetches
	 * If unset, this will default to a new runner (sequential in case of bulk calls)
	 */
	runner?: Runner
	/**
	 * If set, this signal will be attached to all fetch requests
	 * Cannot be set along with timeout
	 */
	signal?: AbortSignal
	/**
	 * Options for how to query around caches
	 */
	cache?:
		| 'default'
		| 'no-cache'
		| 'cache-only'
		| 'cache-only-if-enabled'
		| 'preload'
		| 'preload-if-ssr'
}

export interface ApiCallImpl {
	<T>(url: string, params?: ApiCallParams): Promise<T>
}

export interface ApiCallUrlImpl {
	(url: string, params?: Record<string, JSONData | undefined>): string
}

export class APIError extends Error {
	message: string
	code: number
	extra?: JSONObject
	headers?: Record<string, string>

	url: string
	res?: Response

	constructor(
		url: string,
		message: string,
		code: number,
		extra?: JSONObject,
		headers?: Record<string, string> | Headers,
		res?: Response,
	) {
		super(message)
		this.message = message
		this.code = code
		this.extra = extra
		if (headers instanceof Headers) {
			const obj: Record<string, string> = {}
			headers.forEach((v, k) => {
				if (!isSafeProp(k)) return
				obj['' + k] = v
			})
			headers = obj
		}
		this.headers = headers

		this.url = url
		this.res = res
	}

	static isAPIError(err: unknown): err is APIError {
		return err instanceof APIError
	}
	static isCode<N extends number>(
		err: unknown,
		code: N,
	): err is APIError & { code: N } {
		return APIError.isAPIError(err) && err.code === code
	}
	static isMessage<M extends string>(
		err: unknown,
		msg: M,
	): err is APIError & { message: M } {
		return APIError.isAPIError(err) && err.message === msg
	}
}

const apiCallUrl: ApiCallUrlImpl = function apiCallUrl(
	url: string,
	params?: { [k: string]: JSONData | undefined },
): string {
	if (params) {
		const alreadyReplaced = new Set()
		url = url.replaceAll(
			/:(!?)([a-zA-Z0-9_]+)(\*?)/g,
			(_, unescape, param, star) => {
				alreadyReplaced.add(param)

				let value = params[safeProp(param)]
				if (typeof value == 'number') value = '' + value
				if (typeof value == 'boolean') value = value ? '1' : '0'

				if (typeof value == 'object')
					throw new Error(`Param ${param} is object in path part`)
				if (value == null)
					throw new Error(`Param ${param} is missing in path part`)

				if (unescape) return value
				if (star)
					return value
						.split('/')
						.map(x => encodeURIComponent(x))
						.join('/')
				return encodeURIComponent(value)
			},
		)

		const qs = Object.keys(params)
			.filter(k => params[safeProp(k)] != null && !alreadyReplaced.has(k))
			.map(k => {
				let v = params[safeProp(k)]
				if (v == null) throw new Error('unreachable!')
				if (typeof v == 'object') v = JSON.stringify(v)
				if (typeof v == 'number') v = '' + v
				if (typeof v == 'boolean') v = v ? '1' : '0'

				return [k, v].map(x => encodeURIComponent(x)).join('=')
			})
			.join('&')
		if (qs) url += '?' + qs
	}

	if (!url.startsWith('/')) url = '/' + url
	url = `/api${url}`

	url = url.replaceAll(/\/+/g, '/')

	if (process.env.API_BASE_PATH) {
		url = process.env.API_BASE_PATH + url
	}

	return url
} as ApiCallUrlImpl

/**
 * Makes an API call
 * Checks that the response is `ok` or throws
 * Automatically handles auth and project unless specified, if the globals are set
 */
export const apiCall: ApiCallImpl & {
	url: typeof apiCallUrl
	APIError: typeof APIError
} = Object.assign(
	async function apiCall(
		/**
		 * The API endpoint we're reaching
		 */
		url: string,
		/**
		 * What we're doing to the endpoint
		 */
		{
			body,
			method,
			params,
			headers: extraHeaders,
			raw = false,
			timeout = 0,
			bulk = false,
			runner = bulk ? new SequentialRunner() : parallelRunner,
			signal,
			cache = 'default',
		}: ApiCallParams = {},
	): Promise<JSONPartial | Blob | Response> {
		if (signal?.aborted) throw new Error('Aborted')

		if (cache === 'preload-if-ssr') {
			if (process.env.SSR) cache = 'preload'
			else cache = 'default'
		}

		const headers: Record<string, string> = {}
		let outBody: FormData | Blob | ReadableStream | string | undefined =
			undefined

		if (body !== undefined) {
			if (body instanceof FormData) {
				// application/form-data
				// but the browser sets the type *and* a boundary
				// and we can't do that ourselves
				outBody = body
			} else if (body instanceof Blob) {
				headers['content-type'] =
					body.type || 'application/octet-stream'
				headers['content-length'] = '' + body.size
				outBody = body
			} else if (body instanceof ReadableStream) {
				headers['content-type'] = 'application/octet-stream'
				outBody = body
			} else {
				headers['content-type'] = 'application/json'
				try {
					outBody = JSON.stringify(body)
				} catch (e) {
					if (typeof body == 'object' && !Array.isArray(body)) {
						idAssert<{ [k: string]: JSONData | undefined }>(body)
						for (const [k, v] of Object.entries(body)) {
							if (v === undefined) delete body[safeProp(k)]
						}
						outBody = JSON.stringify(body)
					} else {
						throw e
					}
				}
				headers['content-length'] = '' + outBody.length
			}
		}

		if (bulk) {
			if (raw) throw new TypeError('Cannot use raw bulk api call')
			if (!Array.isArray(params?.keys))
				throw new TypeError('Cannot use bulk api call without keys')
			if (
				typeof bulk == 'number' &&
				(bulk < 1 || Math.trunc(bulk) !== bulk)
			)
				throw new TypeError(
					'Cannot use bulk api call with a nonpositve integer bulk size, gave ' +
						bulk,
				)
			if (typeof bulk == 'boolean') bulk = 100
			if (!(params.keys as Array<JSONData>).length)
				return apiCall(url, {
					body,
					method,
					headers,
					raw: false,
					timeout,
					bulk: false,
					runner,
					params: { ...params },
					signal,
					cache,
				})

			const responses: (JSONPartial | Blob | null)[] = (await Promise.all(
				[...batch(params.keys as Array<JSONData>, bulk)].map(
					async keys => {
						return apiCall(url, {
							body,
							method,
							headers,
							raw: false,
							timeout,
							bulk: false,
							runner,
							params: { ...params, keys },
							signal,
							cache,
						})
					},
				),
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			)) as any
			const result: JSONPartial = {}
			for (const response of responses) {
				if (response instanceof Blob) {
					console.error('Blob part in bulk apiCall', response)
					throw new APIError(url, 'bulk_part_blob', 0)
				}
				if (typeof response != 'object' || Array.isArray(response)) {
					console.error('Non-object part in bulk apiCall', response)
					throw new APIError(url, 'bulk_part_nonobject', 0)
				}
				if (!response) continue
				idAssert<JSONObject>(response)
				for (const [k, v] of Object.entries(response)) {
					if (!(k in result)) {
						result[safeProp(k)] = v
					} else {
						if (
							Array.isArray(v) &&
							Array.isArray(result[safeProp(k)])
						) {
							result[safeProp(k)] = [
								...(result[safeProp(k)] as JSONArray),
								...v,
							]
						} else if (!eq(v, result[safeProp(k)])) {
							throw new APIError(url, 'bulk_conflict', 0, {
								k,
								v,
								p: result[safeProp(k)] as JSONData,
							})
						}
					}
				}
			}
			return result
		}

		if (!url.match(/^https?:\/\//)) url = apiCallUrl(url, params)

		if (!method && body === undefined) method = 'GET'
		if (extraHeaders) {
			for (const k in extraHeaders) {
				headers[safeProp(k)] = '' + extraHeaders[safeProp(k)]
			}
		}
		headers['X-CSRF'] = '1'

		if (cache === 'preload')
			headers['x-cache-options'] = 'cache-only-if-enabled'
		else headers['x-cache-options'] = cache

		const controller =
			!signal && timeout && 'AbortController' in window
				? new AbortController()
				: null
		let timer: number | null = null

		return runner
			.run(async (): Promise<JSONPartial | Blob | Response> => {
				const req = await Promise.race([
					fetch(url, {
						body: outBody,
						method,
						headers,
						signal: signal ?? controller?.signal ?? undefined,
					}),
					new Promise<Response>((_, ko) => {
						if (timeout && controller) {
							timer = window.setTimeout(() => {
								ko(
									new APIError(url, 'api_timeout', 0, {
										timeout,
									}),
								)
								controller.abort()
							}, timeout)
						}
					}),
				])

				if (timer != null) window.clearTimeout(timer)

				if (raw) {
					if (req.ok) return req
					throw new APIError(
						url,
						'raw_error',
						req.status,
						undefined,
						req.headers,
						req,
					)
				}

				if (!req.ok) {
					const ct =
						req.headers.get('Content-Type') ??
						'application/octet-stream'
					if (ct.split(';')[0] !== 'application/json') {
						throw new APIError(
							url,
							'raw_error',
							req.status,
							undefined,
							req.headers,
							req,
						)
					} else {
						const json = await req.json()
						const err =
							typeof json === 'object' &&
							'err' in json &&
							typeof json.err === 'string'
								? json.err
								: 'unknown'
						throw new APIError(
							url,
							err,
							req.status,
							typeof json === 'object' && !Array.isArray(json)
								? json
								: undefined,
							req.headers,
							req,
						)
					}
				}

				const ct =
					req.headers.get('Content-Type') ??
					'application/octet-stream'
				if (ct.split(';')[0] !== 'application/json') {
					const blob = await req.blob()
					return blob
				}

				return await req.json()
			})
			.catch(e => {
				let err = e
				if (
					APIError.isCode(e, 409) &&
					e.headers?.['x-middleware-cache']
				) {
					err = new APIError(
						e.url,
						`cache:${e.headers['x-middleware-cache']}`,
						409,
						e.extra,
						e.headers,
						e.res,
					)
				}
				if (
					APIError.isCode(err, 409) &&
					cache === 'preload' &&
					err.message === 'cache:not-cached'
				) {
					console.log(
						'cache: preload request hit no cache, running preload request',
					)
					void apiCall(url, {
						body,
						method,
						params,
						headers,
						raw,
						timeout,
						bulk,
						signal,
						cache: 'default',
					}).catch(e => {
						console.error('in cache preload', e)
					})
				}
				throw err
			})
	} as ApiCallImpl,
	{ url: apiCallUrl, APIError },
)

if (typeof window !== 'undefined') {
	Object.assign(window, { apiCall })
}
